// umlaut-check: skip — "zuruecksetzen" ist der technische Name aus
// useGemerkteFilter.jsx, bleibt ASCII.

import React, {useEffect, useState} from "react";
import {Container, Offcanvas, OverlayTrigger, Spinner, Tooltip} from "react-bootstrap";
import {Link, usePage} from "@inertiajs/react";
import HeaderWithHelp from "../../Components/SalesAnalysis/HeaderWithHelp.jsx";
import PurposeBox from "../../Components/Cosmos/PurposeBox.jsx";
import SelectList from "../../Components/SelectList.jsx";
import KwSelect from "../../Components/Cosmos/KwSelect.jsx";
import AnsichtSchalter from "../../Components/Cosmos/AnsichtSchalter.jsx";
import LieferplanLegende from "../../Components/Cosmos/LieferplanLegende.jsx";
import LieferplanTabelle from "../../Components/Cosmos/LieferplanTabelle.jsx";
import LieferplanZelleDetail from "../../Components/Cosmos/LieferplanZelleDetail.jsx";
import {AMPEL_LABEL, AmpelPunkt} from "../../Components/Cosmos/AmpelSelect.jsx";
import useAnalysisData from "../../Components/SalesAnalysis/useAnalysisData.js";
import useGemerkteFilter, {AlleFilterZuruecksetzenLink, useGemerkteTabellenScroll} from "../../Components/Cosmos/useGemerkteFilter.jsx";
import {hasAbility} from "../../Components/SalesAnalysis/format.js";
import {TOOLTIP_POPPER_CONFIG} from "../../Components/Cosmos/tooltipConfig.js";

// Befund Sichtprüfung 3 #S19: die Marken heißen MOLOTOW und FLAME (nicht
// "Molotow"/"Flame") - nur das Label ändert sich, "value" bleibt exakt der
// Wert aus cosmos_lieferungen.marke (Ampel-Datei), sonst filtert die
// Auswahl nicht mehr.
const MARKE_OPTIONEN = [
    {value: "", label: "Alle Marken"},
    {value: "Molotow", label: "MOLOTOW"},
    {value: "Burner", label: "BURNER"},
    {value: "Flame", label: "FLAME"},
];

const PRODUKTLINIE_OPTIONEN = [
    {value: "", label: "Alle Produktlinien"},
    {value: "PREMIUM", label: "PREMIUM"},
    {value: "FLAME_ORANGE", label: "FLAME Orange"},
    {value: "FLAME_BLUE", label: "FLAME Blue"},
    {value: "BURNER", label: "BURNER"},
];

const AMPEL_OPTIONEN = ["gruen", "gelb", "rot", "rosa"];

const AMPEL_FILTER_HILFE = {
    gruen: "Nur Farbtöne mit grüner Ampel zeigen (mindestens eine Zelle im sichtbaren Zeitraum).",
    gelb: "Nur Farbtöne mit gelber Ampel zeigen (mindestens eine Zelle im sichtbaren Zeitraum).",
    rot: "Nur Farbtöne mit roter Ampel zeigen (mindestens eine Zelle im sichtbaren Zeitraum).",
    rosa: "Nur Farbtöne mit rosa Ampel zeigen (mindestens eine Zelle im sichtbaren Zeitraum).",
};

/** Klickbarer Filter-Chip einer Ampelfarbe (Mehrfachauswahl, PurposeBox-Konvention: eigenes Bedienelement statt nativer Checkbox-Liste). */
function AmpelChip({farbe, aktiv, onToggle}) {
    return (
        <OverlayTrigger flip popperConfig={TOOLTIP_POPPER_CONFIG} placement="top" overlay={<Tooltip id={`cosmos-ampel-filter-${farbe}`}>{AMPEL_FILTER_HILFE[farbe]}</Tooltip>}>
            <button
                type="button"
                className={`btn btn-sm ${aktiv ? "btn-secondary" : "btn-outline-secondary"} d-inline-flex align-items-center gap-1`}
                aria-pressed={aktiv}
                onClick={() => onToggle(farbe)}
            >
                <AmpelPunkt farbe={farbe} /> {AMPEL_LABEL[farbe]}
            </button>
        </OverlayTrigger>
    );
}

/**
 * Lieferplan-Kalender (AP7, PLAN.md Abschnitt 5 "Lieferplan"): Farbtöne
 * gegen Kalenderwochen mit Oskars Ampel. Filterleiste bleibt beim
 * Nachladen sichtbar, angezeigt wird immer der zuletzt erfolgreich
 * geladene Stand (Hausregel: kein Verschwinden/Aufblitzen).
 */
// T2: "In erster Linie interessieren uns hier immer die Premium-Spraydosen" -
// Standard-Produktlinie dieser Seite ist PREMIUM statt "alle".
const STANDARD_FILTER = {
    marke: "",
    produktlinie: "PREMIUM",
    kw_von: "",
    kw_bis: "",
    suche: "",
    ampel: [],
    nur_abweichungen: false,
    ansicht: "wunsch_zusage",
};

export default function CosmosLieferplan() {
    const benutzerId = usePage().props.auth?.id;
    const {werte, setWert, zuruecksetzen, weichtAb} = useGemerkteFilter("lieferplan", STANDARD_FILTER, {benutzerId});
    const {marke, produktlinie, kw_von: kwVon, kw_bis: kwBis, suche, ampel, nur_abweichungen: nurAbweichungen, ansicht} = werte;
    const [angezeigt, setAngezeigt] = useState(null);
    const [zelleOffen, setZelleOffen] = useState(null);

    const {data, loading, error} = useAnalysisData("/cosmos/daten/lieferplan", {
        marke: marke || undefined,
        produktlinie: produktlinie || undefined,
        kw_von: kwVon || undefined,
        kw_bis: kwBis || undefined,
        suche: suche || undefined,
        ampel: ampel.length ? ampel : undefined,
        nur_abweichungen: nurAbweichungen ? 1 : undefined,
    });

    useEffect(() => {
        if (data) {
            setAngezeigt(data);
        }
    }, [data]);

    // T2 Rücksprung vom Artikel-Blatt: Scroll-Position der Tabelle je Seite merken.
    const tabellenRef = useGemerkteTabellenScroll("lieferplan", Boolean(angezeigt));

    const toggleAmpel = (farbe) => {
        setWert("ampel", ampel.includes(farbe) ? ampel.filter((a) => a !== farbe) : [...ampel, farbe]);
    };

    const canImport = hasAbility(angezeigt?.meta, "canImport");
    const keineDaten = angezeigt && angezeigt.zeilen.length === 0;

    return (
        <Container fluid className="pt-2 pb-4">
            <PurposeBox storageKey="lieferplan">
                Zeigt Farbtöne gegen Kalenderwochen als Kalender: je Zelle die gewünschte, zugesagte, gelieferte
                Menge und den neuen Vorschlag aus dem Block „NEW - POSSIBLE ORDER“. Die Ampelfarbe folgt Oskars
                Excel-Handampel – grün, gelb, rot und rosa; ohne gespeicherte Ampel errechnet die Tabelle einen
                Rückfallwert und kennzeichnet ihn mit gestricheltem Rand.
            </PurposeBox>

            <div className="d-flex align-items-center gap-2 mb-2">
                <h1 className="h4 mb-0">
                    <HeaderWithHelp help="Kalenderansicht: welche Menge je Farbton und Kalenderwoche gewünscht, zugesagt, geliefert und neu vorgeschlagen ist.">
                        Lieferplan
                    </HeaderWithHelp>
                </h1>
                {loading && angezeigt && <Spinner animation="border" size="sm" role="status" aria-label="Wird aktualisiert" />}
            </div>

            {error && <p className="text-danger">{error}</p>}

            <LieferplanLegende />

            <div className="d-flex flex-wrap align-items-end gap-3 mb-2">
                <div>
                    <HeaderWithHelp help="Schränkt die Kalenderwochen auf Lieferungen dieser Marke ein.">
                        <span className="small text-muted d-block mb-1">Marke</span>
                    </HeaderWithHelp>
                    <SelectList value={marke} options={MARKE_OPTIONEN} onChange={(wert) => setWert("marke", wert)} size="sm" ariaLabel="Marke" style={{minWidth: 160}} />
                </div>
                <div>
                    <HeaderWithHelp help="Zeigt nur Farbtöne dieser Produktlinie.">
                        <span className="small text-muted d-block mb-1">Produktlinie</span>
                    </HeaderWithHelp>
                    <SelectList
                        value={produktlinie}
                        options={PRODUKTLINIE_OPTIONEN}
                        onChange={(wert) => setWert("produktlinie", wert)}
                        size="sm"
                        ariaLabel="Produktlinie"
                        style={{minWidth: 190}}
                    />
                </div>
                <div>
                    <HeaderWithHelp help="Erste angezeigte Kalenderwoche (Standard: vier Wochen zurück).">
                        <span className="small text-muted d-block mb-1">KW von</span>
                    </HeaderWithHelp>
                    <KwSelect value={kwVon} onChange={(wert) => setWert("kw_von", wert)} ariaLabel="Kalenderwoche von" />
                </div>
                <div>
                    <HeaderWithHelp help="Letzte angezeigte Kalenderwoche (Standard: zwölf Wochen voraus).">
                        <span className="small text-muted d-block mb-1">KW bis</span>
                    </HeaderWithHelp>
                    <KwSelect value={kwBis} onChange={(wert) => setWert("kw_bis", wert)} ariaLabel="Kalenderwoche bis" />
                </div>
                <div>
                    <HeaderWithHelp help="Sucht in Farbnummer, Farbname und Artikelnummer.">
                        <span className="small text-muted d-block mb-1">Suche</span>
                    </HeaderWithHelp>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        style={{minWidth: 200}}
                        placeholder="Farbnummer, Name oder Artikelnr."
                        value={suche}
                        onChange={(e) => setWert("suche", e.target.value)}
                    />
                </div>
                <div className="form-check form-switch mb-1">
                    <input
                        id="cosmos-lieferplan-abweichungen"
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={nurAbweichungen}
                        onChange={(e) => setWert("nur_abweichungen", e.target.checked)}
                    />
                    <HeaderWithHelp help="Blendet Farbtöne aus, bei denen Zusage und Wunsch in jeder angezeigten Woche übereinstimmen.">
                        <label htmlFor="cosmos-lieferplan-abweichungen" className="form-check-label small">
                            Nur Abweichungen
                        </label>
                    </HeaderWithHelp>
                </div>
            </div>

            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span className="small text-muted">Ampel-Filter:</span>
                {AMPEL_OPTIONEN.map((farbe) => (
                    <AmpelChip key={farbe} farbe={farbe} aktiv={ampel.includes(farbe)} onToggle={toggleAmpel} />
                ))}
                <span className="vr mx-1" />
                <AnsichtSchalter value={ansicht} onChange={(wert) => setWert("ansicht", wert)} />
                <AlleFilterZuruecksetzenLink sichtbar={weichtAb} onClick={zuruecksetzen} />
            </div>

            {angezeigt && keineDaten && (
                <div className="alert alert-light border small d-flex justify-content-between align-items-center">
                    <span>Keine Lieferungen für diese Auswahl.</span>
                    {canImport && (
                        <Link href="/cosmos/lieferungen" className="btn btn-sm btn-outline-primary">
                            Datei importieren
                        </Link>
                    )}
                </div>
            )}

            {angezeigt && !keineDaten && (
                <LieferplanTabelle
                    zeilen={angezeigt.zeilen}
                    wochen={angezeigt.wochen}
                    ansicht={ansicht}
                    canArtikel={hasAbility(angezeigt.meta, "canArtikel")}
                    canLieferungen={hasAbility(angezeigt.meta, "canLieferungen")}
                    onZelleKlick={(artikelNummer, kw) => setZelleOffen({artikelNummer, kw})}
                    tabellenRef={tabellenRef}
                />
            )}

            {!angezeigt && loading && (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Wird geladen…</span>
                    </Spinner>
                </div>
            )}

            {/* N6 (zweite Sichtprüfung): 480px schnitt die Positionstabelle an - breiter, deckelt sich auf schmalen Bildschirmen selbst über Bootstraps Offcanvas-Regeln. */}
            <Offcanvas show={Boolean(zelleOffen)} onHide={() => setZelleOffen(null)} placement="end" style={{width: "min(640px, 100vw)"}}>
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title className="h6 mb-0">Zellen-Detail</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {zelleOffen && (
                        <LieferplanZelleDetail
                            artikelNummer={zelleOffen.artikelNummer}
                            kw={zelleOffen.kw}
                            canEdit={hasAbility(angezeigt?.meta, "canEdit")}
                            canArtikel={hasAbility(angezeigt?.meta, "canArtikel")}
                            canLieferungen={hasAbility(angezeigt?.meta, "canLieferungen")}
                        />
                    )}
                </Offcanvas.Body>
            </Offcanvas>
        </Container>
    );
}
